import { ChangeEvent, SyntheticEvent, useState, useContext } from "react";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { InputNumber } from "primereact/inputnumber";
import { Dropdown, DropdownChangeEvent } from "primereact/dropdown";
import { Checkbox } from "primereact/checkbox";
import { Panel } from "primereact/panel";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "react-query";
import { useCreateFieldType } from "../../../api/queries/field-types/useCreateFieldType";
import {
  DataTypeEnum,
  DataTypeLabels,
  CreateFieldTypePayload,
  FieldType,
  ReferenceEntityName,
  ReferenceEntityLabels,
} from "../../../api/types/field-type";
import { queryKeys, defaultInvalidateQueryFilters } from "../../../api/query";
import { appToastContext } from "../../../context/toast";
import AutoCompleteFieldType from "../../Common/AutoCompleteFieldType";

export default function FieldTypeForm() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { ref: appToastRef } = useContext(appToastContext);

  const createFieldType = useCreateFieldType();

  const [name, setName] = useState<string>("");
  const [type, setType] = useState<DataTypeEnum>(DataTypeEnum.String);
  const [required, setRequired] = useState<boolean>(false);
  const [defaultValue, setDefaultValue] = useState<string>("");
  const [active, setActive] = useState<boolean>(true);
  const [weight, setWeight] = useState<number | null>(null);
  const [parentFieldType, setParentFieldType] = useState<FieldType | null>(
    null,
  );
  const [allowMultiple, setAllowMultiple] = useState<boolean>(false);

  // Number validation fields
  const [decimalCharacters, setDecimalCharacters] = useState<number>(0);
  const [minNumber, setMinNumber] = useState<number | null>(null);
  const [maxNumber, setMaxNumber] = useState<number | null>(null);

  // Image validation fields
  const [maxSize, setMaxSize] = useState<number | null>(null);
  const [minWidth, setMinWidth] = useState<number | null>(null);
  const [maxWidth, setMaxWidth] = useState<number | null>(null);
  const [minHeight, setMinHeight] = useState<number | null>(null);
  const [maxHeight, setMaxHeight] = useState<number | null>(null);
  const [isAvatarConfig, setIsAvatarConfig] = useState<boolean>(false);

  // List items state
  const [listItems, setListItems] = useState<string[]>([]);
  const [currentListItem, setCurrentListItem] = useState<string>("");
  const [allowUnlisted, setAllowUnlisted] = useState<boolean>(false);

  const [referenceEntity, setReferenceEntity] =
    useState<ReferenceEntityName | null>(null);

  const onNameChange = (event: ChangeEvent<HTMLInputElement>): void =>
    setName(event.target.value);

  const onTypeChange = (event: DropdownChangeEvent) => setType(event.value);

  const onDefaultValueChange = (event: ChangeEvent<HTMLInputElement>): void =>
    setDefaultValue(event.target.value);

  const onCurrentListItemChange = (
    event: ChangeEvent<HTMLInputElement>,
  ): void => setCurrentListItem(event.target.value);

  const addListItem = (): void => {
    if (currentListItem.trim() && !listItems.includes(currentListItem.trim())) {
      setListItems([...listItems, currentListItem.trim()]);
      setCurrentListItem("");
    }
  };

  const removeListItem = (index: number): void => {
    setListItems(listItems.filter((_, i) => i !== index));
  };

  const handleKeyPress = (
    event: React.KeyboardEvent<HTMLInputElement>,
  ): void => {
    if (event.key === "Enter") {
      event.preventDefault();
      addListItem();
    }
  };

  const handleSubmit = async (event: SyntheticEvent) => {
    event.preventDefault();

    const payload: CreateFieldTypePayload = {
      name,
      type,
      required,
      default_value: defaultValue.trim() || null,
      active,
      weight,
      parent_id: parentFieldType?.id || null,
    };

    // Add number validation fields if type is Number or NumberWithDecimals
    if (
      type === DataTypeEnum.Number ||
      type === DataTypeEnum.NumberWithDecimals
    ) {
      if (minNumber !== null) payload.min_number = minNumber;
      if (maxNumber !== null) payload.max_number = maxNumber;
      if (type === DataTypeEnum.NumberWithDecimals) {
        payload.decimal_characters = decimalCharacters;
      }
    }

    if (type === DataTypeEnum.List) {
      payload.list_items = listItems;
      payload.allow_unlisted = allowUnlisted;
    }

    if (type === DataTypeEnum.Reference && referenceEntity) {
      payload.reference_entity = referenceEntity;
    }

    // Add allowMultiple if type is List or Reference
    if (type === DataTypeEnum.List || type === DataTypeEnum.Reference) {
      payload.allow_multiple = allowMultiple;
    }

    // Add image/video validation fields
    if (type === DataTypeEnum.Image || type === DataTypeEnum.Video) {
      if (maxSize !== null) payload.max_size = maxSize * 1024;
    }

    if (type === DataTypeEnum.Image) {
      if (minWidth !== null) payload.min_width = minWidth;
      if (maxWidth !== null) payload.max_width = maxWidth;
      if (minHeight !== null) payload.min_height = minHeight;
      if (maxHeight !== null) payload.max_height = maxHeight;
      payload.is_avatar_config = isAvatarConfig;
    }

    try {
      await createFieldType.mutateAsync(payload);

      queryClient.invalidateQueries(
        queryKeys.fieldTypes(),
        defaultInvalidateQueryFilters,
      );

      appToastRef?.current?.show({
        severity: "success",
        summary: "Success",
        detail: `Field type "${name}" has been created successfully`,
      });

      navigate("/crm/field-types");
    } catch (error) {
      createFieldType.reset(); // Reset the mutation state
      appToastRef?.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to create field type. Please try again.",
      });
    }
  };

  const isNumberType =
    type === DataTypeEnum.Number || type === DataTypeEnum.NumberWithDecimals;
  const isListType = type === DataTypeEnum.List;
  const isReferenceType = type === DataTypeEnum.Reference;
  const isImageType =
    type === DataTypeEnum.Image || (type as string) === "Image";
  const isVideoType =
    type === DataTypeEnum.Video || (type as string) === "Video";
  const supportsMultipleSelection = isListType || isReferenceType;

  const dataTypeOptions = Object.entries(DataTypeLabels).map(
    ([value, label]) => ({
      label,
      value: value as DataTypeEnum,
    }),
  );

  const referenceEntityOptions = Object.entries(ReferenceEntityLabels).map(
    ([value, label]) => ({
      label,
      value: value as ReferenceEntityName,
    }),
  );

  return (
    <form onSubmit={handleSubmit} className="col-12">
      <Panel header="Create Field Type" toggleable={true} className="mb-3">
        <div className="p-fluid">
          <div className="field pt-3">
            <div className="p-float-label">
              <InputText value={name} onChange={onNameChange} required />
              <label>Name *</label>
            </div>
          </div>

          <div className="field pt-3">
            <div className="p-float-label">
              <Dropdown
                value={type}
                onChange={onTypeChange}
                options={dataTypeOptions}
                filter={true}
              />
              <label>Type *</label>
            </div>
          </div>

          <div className="field pt-3">
            <div className="p-float-label">
              <AutoCompleteFieldType
                value={parentFieldType}
                onChange={setParentFieldType}
                placeholder="Search for parent field type..."
              />
              <label>Parent Field Type</label>
            </div>
          </div>

          <div className="field pt-3">
            <div className="p-field-checkbox">
              <Checkbox
                inputId="required"
                checked={required}
                onChange={(e) => setRequired(e.checked || false)}
              />
              <label htmlFor="required" className="p-checkbox-label ml-2">
                Required
              </label>
            </div>
          </div>

          {type !== DataTypeEnum.Group && (
            <div className="field pt-3">
              <div className="p-float-label">
                <InputText
                  value={defaultValue}
                  onChange={onDefaultValueChange}
                />
                <label>Default Value</label>
              </div>
            </div>
          )}

          <div className="field pt-3">
            <div className="p-field-checkbox">
              <Checkbox
                inputId="active"
                checked={active}
                onChange={(e) => setActive(e.checked || false)}
              />
              <label htmlFor="active" className="p-checkbox-label ml-2">
                Active
              </label>
            </div>
          </div>

          {supportsMultipleSelection && (
            <div className="field pt-3">
              <div className="p-field-checkbox">
                <Checkbox
                  inputId="allowMultiple"
                  checked={allowMultiple}
                  onChange={(e) => setAllowMultiple(e.checked || false)}
                />
                <label
                  htmlFor="allowMultiple"
                  className="p-checkbox-label ml-2"
                >
                  Allow Multiple Choices
                </label>
              </div>
            </div>
          )}

          <div className="field pt-3">
            <div className="p-float-label">
              <InputNumber
                value={weight}
                onChange={(e) => setWeight(e.value)}
                showButtons={false}
                mode="decimal"
                minFractionDigits={0}
                maxFractionDigits={10}
              />
              <label>Weight</label>
            </div>
          </div>

          {/* Number validation fields */}
          {isNumberType && (
            <>
              <div className="field pt-3">
                <div className="p-float-label">
                  <InputNumber
                    value={minNumber}
                    onChange={(e) => setMinNumber(e.value)}
                    showButtons={false}
                  />
                  <label>Min Number</label>
                </div>
              </div>

              <div className="field pt-3">
                <div className="p-float-label">
                  <InputNumber
                    value={maxNumber}
                    onChange={(e) => setMaxNumber(e.value)}
                    showButtons={false}
                  />
                  <label>Max Number</label>
                </div>
              </div>

              {type === DataTypeEnum.NumberWithDecimals && (
                <div className="field pt-3">
                  <div className="p-float-label">
                    <InputNumber
                      value={decimalCharacters}
                      onChange={(e) => setDecimalCharacters(e.value || 0)}
                      min={0}
                      max={10}
                      showButtons={true}
                    />
                    <label>Decimal Characters</label>
                  </div>
                </div>
              )}
            </>
          )}

          {/* List items fields */}
          {isListType && (
            <>
              <div className="field pt-3">
                <label
                  htmlFor="list-item-input"
                  className="block text-900 font-medium mb-2"
                >
                  List Items
                </label>
                <div className="p-inputgroup">
                  <InputText
                    id="list-item-input"
                    value={currentListItem}
                    onChange={onCurrentListItemChange}
                    onKeyPress={handleKeyPress}
                    placeholder="Enter list item"
                  />
                  <Button
                    type="button"
                    icon="pi pi-plus"
                    onClick={addListItem}
                    disabled={
                      !currentListItem.trim() ||
                      listItems.includes(currentListItem.trim())
                    }
                    className="p-button-secondary"
                  />
                </div>
              </div>

              {listItems.length > 0 && (
                <div className="field">
                  <label className="block text-900 font-medium mb-2">
                    Added Items ({listItems.length})
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {listItems.map((item, index) => (
                      <div
                        key={index}
                        className="p-badge p-badge-secondary flex align-items-center gap-2 p-2"
                      >
                        <span>{item}</span>
                        <Button
                          type="button"
                          icon="pi pi-times"
                          onClick={() => removeListItem(index)}
                          className="p-button-text p-button-sm p-0 text-white"
                          style={{ fontSize: "0.75rem", padding: "0.25rem" }}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="field pt-3">
                <div className="p-field-checkbox">
                  <Checkbox
                    inputId="allowUnlisted"
                    checked={allowUnlisted}
                    onChange={(e) => setAllowUnlisted(e.checked || false)}
                  />
                  <label
                    htmlFor="allowUnlisted"
                    className="p-checkbox-label ml-2"
                  >
                    Allow unlisted values
                  </label>
                </div>
              </div>
            </>
          )}

          {isReferenceType && (
            <div className="field pt-3">
              <div className="p-float-label">
                <Dropdown
                  value={referenceEntity}
                  onChange={(e) => setReferenceEntity(e.value)}
                  options={referenceEntityOptions}
                  placeholder="Select Reference Entity"
                />
                <label>Reference Entity *</label>
              </div>
            </div>
          )}

          {(isImageType || isVideoType) && (
            <>
              <div className="field pt-3">
                <div className="p-float-label">
                  <InputNumber
                    value={maxSize}
                    onChange={(e) => setMaxSize(e.value)}
                    showButtons={false}
                    suffix=" MB"
                    minFractionDigits={0}
                    maxFractionDigits={2}
                  />
                  <label>Max Size (MB)</label>
                </div>
              </div>
              {isImageType && (
                <div className="formgrid grid">
                  <div className="field col-6 pt-3">
                    <div className="p-float-label">
                      <InputNumber
                        value={minWidth}
                        onChange={(e) => setMinWidth(e.value)}
                        showButtons={false}
                        suffix=" px"
                      />
                      <label>Min Width (px)</label>
                    </div>
                  </div>
                  <div className="field col-6 pt-3">
                    <div className="p-float-label">
                      <InputNumber
                        value={maxWidth}
                        onChange={(e) => setMaxWidth(e.value)}
                        showButtons={false}
                        suffix=" px"
                      />
                      <label>Max Width (px)</label>
                    </div>
                  </div>
                  <div className="field col-6 pt-3">
                    <div className="p-float-label">
                      <InputNumber
                        value={minHeight}
                        onChange={(e) => setMinHeight(e.value)}
                        showButtons={false}
                        suffix=" px"
                      />
                      <label>Min Height (px)</label>
                    </div>
                  </div>
                  <div className="field col-6 pt-3">
                    <div className="p-float-label">
                      <InputNumber
                        value={maxHeight}
                        onChange={(e) => setMaxHeight(e.value)}
                        showButtons={false}
                        suffix=" px"
                      />
                      <label>Max Height (px)</label>
                    </div>
                  </div>
                  <div className="field col-12 pt-3">
                    <div className="p-field-checkbox">
                      <Checkbox
                        inputId="isAvatarConfig"
                        checked={isAvatarConfig}
                        onChange={(e) => setIsAvatarConfig(e.checked || false)}
                      />
                      <label
                        htmlFor="isAvatarConfig"
                        className="p-checkbox-label ml-2"
                      >
                        Allow setting as Avatar
                      </label>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </Panel>

      <div className="flex gap-1 justify-content-end">
        <Button
          type="button"
          label="Cancel"
          className="p-button-secondary p-button-sm"
          onClick={() => navigate("/crm/field-types")}
        />

        <Button
          type="submit"
          label="Create"
          className="p-button-main p-button-sm"
          disabled={
            !name ||
            !type ||
            (isListType && listItems.length === 0) ||
            (isReferenceType && !referenceEntity) ||
            createFieldType.isLoading
          }
          loading={createFieldType.isLoading}
        />
      </div>
    </form>
  );
}
